import { useCallback } from 'react';
import { useRequiredParamsSetter } from '../../../../context/TargetingSettingsContext.tsx';
import {
  type Element,
  type RequiredParamsType,
  RequiredTargetingSectionKeys,
  TargetingElementKeys,
} from '../../../../context/types.ts';
import { useDeviceInitialState } from '../../../../hooks/initialState/useDeviceInitialState.tsx';
import { useFormatInitialState } from '../../../../hooks/initialState/useFormatInitialState.tsx';
import { useElementOperations } from '../../../../hooks/useElementOperations.ts';
import { useFormatOperations } from '../../../../hooks/useFormatOperations.ts';
import { enableTVDevices } from '../utils/enableTVDevices.ts';
import { restoreDefaultOnClickToSection } from '../utils/restoreDefaultOnClickToSection.ts';
import { useOneTimeHandlers } from './useOneTimeHandlers.ts';

export const useCTVPopupActions = () => {
  const set = useRequiredParamsSetter();
  const { handleCTVSelect } = useFormatOperations();
  const { getOneTimeHandlers } = useOneTimeHandlers();
  const { disableElementByKey, enableElementByKey } = useElementOperations();
  const deviceInitialState = useDeviceInitialState();
  const formatInitialState = useFormatInitialState();

  const getDeviceOneTimeHandlers = useCallback(
    (prev: RequiredParamsType): Element[] =>
      getOneTimeHandlers({
        prev,
        sectionKey: RequiredTargetingSectionKeys.DEVICE,
        oneTimeAction: (set, sectionKey) => enableTVDevices(sectionKey, set, enableElementByKey),
        initialState: deviceInitialState,
      }),
    [getOneTimeHandlers, enableElementByKey, deviceInitialState]
  );

  const getFormatOneTimeHandlers = useCallback(
    (prev: RequiredParamsType): Element[] =>
      getOneTimeHandlers({
        prev,
        sectionKey: RequiredTargetingSectionKeys.FORMAT,
        oneTimeAction: set =>
          restoreDefaultOnClickToSection({
            sectionKey: RequiredTargetingSectionKeys.DEVICE,
            defaultState: deviceInitialState,
            set,
          }),
        initialState: formatInitialState,
      }),
    [getOneTimeHandlers, deviceInitialState, formatInitialState]
  );

  const onConfirmed = useCallback(() => {
    set((prev: RequiredParamsType) => {
      handleCTVSelect(prev.sections);

      return { ...prev };
    });
  }, [handleCTVSelect, set]);

  const onCancel = useCallback(() => {
    set((prev: RequiredParamsType) => {
      const updatedFormatElements = getFormatOneTimeHandlers(prev);
      const updatedDeviceElements = disableElementByKey(getDeviceOneTimeHandlers(prev), TargetingElementKeys.DEVICE_TV);

      return {
        ...prev,
        sections: {
          ...prev.sections,
          [RequiredTargetingSectionKeys.FORMAT]: {
            ...prev.sections[RequiredTargetingSectionKeys.FORMAT],
            elements: updatedFormatElements,
          },
          [RequiredTargetingSectionKeys.DEVICE]: {
            ...prev.sections[RequiredTargetingSectionKeys.DEVICE],
            elements: updatedDeviceElements,
          },
        },
      };
    });
  }, [set, getFormatOneTimeHandlers, getDeviceOneTimeHandlers, disableElementByKey]);

  return { onConfirmed, onCancel };
};
